import { useCallback, useEffect, useRef, useState } from 'react';

export default function useGameLoopContext({
  fps,
  gameConf,
  initialState = {},
  prepareGameData,
}) {
  // This will be used just to force a render on all the children every frame
  const [frameId, render] = useState(0);
  const frameDuration = 1000 / fps;

  // This ref will be the actual game loop state
  const loopRef = useRef({
    ...initialState,
    gameConf: null,
    frame: 0,
    time: 0,
    deltaTime: 0,
    fps,
    paused: false,
    isReady: false,
  });

  const actions = {
    setState: (newPartialState) => {
      loopRef.current = { ...loopRef.current, ...newPartialState };
    },
    pause: () => {
      cancelAnimationFrame(loopRef.current.frame);
      loopRef.current = {
        ...loopRef.current,
        paused: true,
      };
    },
    resume: () => {
      loopRef.current = {
        ...loopRef.current,
        time: performance.now(),
        frame: requestAnimationFrame(update),
        paused: false,
      };
    },
  };

  const update = useCallback((time) => {
    const deltaTime = time - loopRef.current.time;
    let frameId = frameId;

    if (!loopRef.current.paused) {
      frameId = requestAnimationFrame(update);
    }
    if (deltaTime >= frameDuration) {
      const frame = loopRef.current.frame + 1;
      loopRef.current = {
        ...loopRef.current,
        time,
        deltaTime,
        frame,
      };
      // We need to set state to force a render on all the children every frame
      render(frameId);
    }
  });

  useEffect(() => {
    loopRef.current = {
      ...loopRef.current,
      time: performance.now(),
      frame: requestAnimationFrame(update),
    };
    return () => cancelAnimationFrame(loopRef.current.frame);
  }, []);

  useEffect(() => {
    if (!loopRef.current.isReady) {
      if (prepareGameData) {
        actions.setState({
          gameConf: prepareGameData(gameConf),
          isReady: true,
        });
      } else {
        actions.setState({ gameConf, isReady: true });
      }
    }
  }, [gameConf, prepareGameData, loopRef.current.isReady]);

  return {
    state: loopRef.current,
    actions,
  };
}
